import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    Divider,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Warning as WarningIcon,
    ReceiptLong as LedgerIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../../hooks/useFetch';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';
import TablePagination from '../../UserList/TablePagination';
import StudentLedger from './StudentLedger';

function ReceivablesTracker() {
    const theme = useTheme();
    const [filters, setFilters] = useState({
        student_id: '',
        course_id: '',
        overdue_only: false,
        archive_status: 'active', // 'active' | 'archived' | 'all'
    });
    const [studentOption, setStudentOption] = useState(null);
    const [courseOption, setCourseOption] = useState(null);
    const [ledgerEnrollment, setLedgerEnrollment] = useState(null);
    const [page, setPage] = useState(0); // 0-based
    const [rowsPerPage, setRowsPerPage] = useState(15);

    // Fetch courses for dropdown
    const { data: coursesData } = useFetch('/operations/api/v1/teachers-and-courses/');
    const courses = coursesData?.results || [];

    // Build API params
    const apiParams = useMemo(() => {
        const params = { status: filters.archive_status || 'active' };
        if (filters.student_id) params.student_id = filters.student_id;
        if (filters.course_id) params.course_id = filters.course_id;
        return params;
    }, [filters]);

    const { data: receivablesData, loading, error } = useFetch(API.receivablesSummary, { 
        params: apiParams,
    });

    const receivables = useMemo(() => receivablesData || [], [receivablesData]);
    
    // Filter for overdue accounts (total_remaining > 0 and no recent payment)
    const filteredReceivables = useMemo(() => {
        let filtered = receivables;
        
        if (filters.overdue_only) {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            filtered = receivables.filter(item => {
                const hasRemaining = (item.total_remaining || 0) > 0;
                if (!hasRemaining) return false;
                
                // Consider overdue if no payment in last 30 days or never paid
                if (!item.last_payment_date) return true;
                
                const lastPayment = new Date(item.last_payment_date);
                return lastPayment < thirtyDaysAgo;
            });
        }
        
        return filtered;
    }, [receivables, filters.overdue_only]);

    const totalReceivables = useMemo(() => {
        return filteredReceivables.reduce((sum, item) => sum + (item.total_remaining || 0), 0);
    }, [filteredReceivables]);

    const overdueCount = useMemo(() => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        return receivables.filter(item => {
            const hasRemaining = (item.total_remaining || 0) > 0;
            if (!hasRemaining) return false;
            
            if (!item.last_payment_date) return true;
            
            const lastPayment = new Date(item.last_payment_date);
            return lastPayment < thirtyDaysAgo;
        }).length;
    }, [receivables]);

    const hasActiveFilters = filters.student_id || filters.course_id || filters.overdue_only || filters.archive_status !== 'active';

    // Client-side pagination of the (filtered) rows; aggregates stay full-set.
    const paginatedReceivables = useMemo(
        () => filteredReceivables.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [filteredReceivables, page, rowsPerPage]
    );

    // Snap back to the first page whenever the filtered set changes.
    useEffect(() => {
        setPage(0);
    }, [filters.student_id, filters.course_id, filters.overdue_only, filters.archive_status]);

    const handleFilterChange = (field, value, option = null) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        if (field === 'student_id') {
            setStudentOption(option);
        } else if (field === 'course_id') {
            setCourseOption(option);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            student_id: '',
            course_id: '',
            overdue_only: false,
            archive_status: 'active',
        });
        setStudentOption(null);
        setCourseOption(null);
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const isOverdue = (item) => {
        if ((item.total_remaining || 0) <= 0) return false;
        
        if (!item.last_payment_date) return true; // Never paid
        
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const lastPayment = new Date(item.last_payment_date);
        
        return lastPayment < thirtyDaysAgo;
    };

    const columns = [
        {
            id: 'student_name',
            label: 'هنرجو',
            align: 'start',
            render: (item) => (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontWeight: 'medium',
                        color: isOverdue(item) ? 'error.main' : 'inherit'
                    }}
                >
                    {item.student_name || '-'}
                </Typography>
            )
        },
        {
            id: 'course_name',
            label: 'دوره',
            align: 'start',
            priority: 'secondary',
            render: (item) => (
                <Typography variant="body2">
                    {item.course_name || '-'}
                </Typography>
            )
        },
        {
            id: 'remained_sessions',
            label: 'جلسات\nباقیمانده',
            priority: 'secondary',
            render: (item) => (
                <Typography variant="body2">
                    {toPersianDigits(item.remained_sessions || 0)}
                </Typography>
            )
        },
        {
            id: 'per_session_price',
            label: 'قیمت\nهر جلسه',
            priority: 'secondary',
            render: (item) => (
                <Typography variant="body2">
                    {formatCurrency(item.per_session_price)} تومان
                </Typography>
            )
        },
        {
            id: 'total_remaining',
            label: 'مبلغ\nباقیمانده',
            render: (item) => (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontWeight: 'bold',
                        color: (item.total_remaining || 0) > 0 
                            ? (isOverdue(item) ? 'error.main' : 'warning.main')
                            : 'success.main'
                    }}
                >
                    {formatCurrency(item.total_remaining)} تومان
                </Typography>
            )
        },
        {
            id: 'last_payment_date',
            label: 'آخرین\nپرداخت',
            priority: 'secondary',
            render: (item) => (
                <Typography 
                    variant="body2"
                    color={item.last_payment_date ? 'text.secondary' : 'text.disabled'}
                >
                    {formatDate(item.last_payment_date)}
                </Typography>
            )
        },
        {
            id: 'status',
            label: 'وضعیت',
            compact: true,
            render: (item) => {
                if ((item.total_remaining || 0) <= 0) {
                    return (
                        <Chip
                            label="پرداخت شده"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    );
                }
                if (isOverdue(item)) {
                    return (
                        <Chip
                            icon={<WarningIcon />}
                            label="معوق"
                            size="small"
                            color="error"
                        />
                    );
                }
                return (
                    <Chip
                        label="در انتظار پرداخت"
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                );
            }
        },
        {
            id: 'ledger',
            label: 'صورت‌حساب',
            compact: true,
            render: (item) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LedgerIcon />}
                    onClick={() => setLedgerEnrollment(item)}
                >
                    صورت‌حساب
                </Button>
            ),
        },
    ];

    if (ledgerEnrollment) {
        return (
            <RTL>
                <StudentLedger enrollment={ledgerEnrollment} onBack={() => setLedgerEnrollment(null)} />
            </RTL>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        پیگیری مطالبات هنرجویان
                    </Typography>
                </Box>

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                کل مطالبات
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {formatCurrency(totalReceivables)} تومان
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText' }}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                تعداد حساب‌های معوق
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {toPersianDigits(overdueCount)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'info.main', color: 'info.contrastText' }}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                تعداد کل حساب‌ها
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {toPersianDigits(filteredReceivables.length)}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">فیلترها</Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{ mr: 'auto' }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid 
                        container 
                        spacing={2}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={4}>
                            <SearchableAutocomplete
                                apiUrl="/accounts/api/v1/profile/students/"
                                getOptionLabel={(option) => 
                                    `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                                }
                                getOptionValue={(option) => option?.user?.id}
                                value={filters.student_id || null}
                                onChange={(value, option) => handleFilterChange('student_id', value || '', option)}
                                initialOption={studentOption}
                                label="هنرجو"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>دوره</InputLabel>
                                <Select
                                    value={filters.course_id}
                                    label="دوره"
                                    onChange={(e) => {
                                        const selectedCourse = courses.find(c => c.id === e.target.value);
                                        handleFilterChange('course_id', e.target.value || '', selectedCourse || null);
                                    }}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    {courses.map((course) => (
                                        <MenuItem key={course.id} value={course.id}>
                                            {course.course_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>وضعیت پرداخت</InputLabel>
                                <Select
                                    value={filters.overdue_only ? 'overdue' : 'all'}
                                    label="وضعیت پرداخت"
                                    onChange={(e) => handleFilterChange('overdue_only', e.target.value === 'overdue')}
                                >
                                    <MenuItem value="all">همه</MenuItem>
                                    <MenuItem value="overdue">فقط معوق</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>وضعیت ثبت‌نام</InputLabel>
                                <Select
                                    value={filters.archive_status}
                                    label="وضعیت ثبت‌نام"
                                    onChange={(e) => handleFilterChange('archive_status', e.target.value)}
                                >
                                    <MenuItem value="active">فعال</MenuItem>
                                    <MenuItem value="archived">آرشیوشده</MenuItem>
                                    <MenuItem value="all">همه</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Overdue Warning */}
                {!loading && overdueCount > 0 && !filters.overdue_only && (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                        {toPersianDigits(overdueCount)} حساب معوق وجود دارد. برای مشاهده فقط حساب‌های معوق، فیلتر &quot;فقط معوق&quot; را فعال کنید.
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Count */}
                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            تعداد نتایج: {toPersianDigits(filteredReceivables.length)}
                        </Typography>
                    </Box>
                )}

                {/* Receivables Table */}
                {!loading && (
                    <>
                        <StandardTable
                            columns={columns}
                            rows={paginatedReceivables}
                            loading={false}
                            error={error}
                            emptyText="مطالبه‌ای یافت نشد."
                            ariaLabel="جدول مطالبات هنرجویان"
                        />
                        {filteredReceivables.length > 0 && (
                            <Paper variant="outlined" sx={{ mt: 1 }}>
                                <TablePagination
                                    totalCount={filteredReceivables.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={(newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    responsive
                                />
                            </Paper>
                        )}
                    </>
                )}
            </Box>
        </RTL>
    );
}

export default ReceivablesTracker;

